import React from 'react';
import { Button, Select, Slider, Form, Card, Space, Tooltip, InputNumber, Row, Col } from 'antd';
import { 
  SwapOutlined, 
  ArrowUpOutlined,
  TrophyOutlined,
  SortAscendingOutlined,
  UsergroupAddOutlined,
  SettingOutlined
} from '@ant-design/icons';

const { Option } = Select;

const SortingOptions = ({ 
  sortingMode, 
  onSortingModeChange, 
  onArrange, 
  isArranging,
  rows,
  cols,
  onRowsChange,
  onColsChange 
}) => {
  
  const sortingModes = [
    {
      value: 'random',
      label: '随机排序',
      icon: <SwapOutlined />,
      description: '完全随机分配座位，适合日常使用',
      color: '#1890ff'
    },
    {
      value: 'height',
      label: '身高排序',
      icon: <ArrowUpOutlined />,
      description: '按身高从低到高排序，前排坐矮的同学',
      color: '#52c41a'
    },
    {
      value: 'grade',
      label: '成绩排序',
      icon: <TrophyOutlined />,
      description: '按成绩从高到低排序，成绩好的坐前排',
      color: '#faad14'
    },
    {
      value: 'alphabetical',
      label: '姓名排序',
      icon: <SortAscendingOutlined />,
      description: '按姓名拼音顺序排序',
      color: '#722ed1'
    },
    {
      value: 'gender_balanced',
      label: '男女均衡',
      icon: <UsergroupAddOutlined />,
      description: '尽可能让男女生分布均匀',
      color: '#eb2f96'
    }
  ];


  return (
    <div className="space-y-4">
      {/* 教室配置 */}
      <Card 
        title={
          <span>
            <SettingOutlined className="mr-2" />
            教室配置
          </span>
        } 
        size="small"
      >
        <Row gutter={16}>
          <Col span={12}>
            <div className="text-sm text-gray-600 mb-2">行数</div>
            <InputNumber
              min={3}
              max={12}
              value={rows}
              onChange={onRowsChange}
              className="w-full"
              addonAfter="行"
            />
          </Col>
          <Col span={12}>
            <div className="text-sm text-gray-600 mb-2">列数</div>
            <InputNumber
              min={4}
              max={12}
              value={cols}
              onChange={onColsChange}
              className="w-full"
              addonAfter="列"
            />
          </Col>
        </Row>
        
        <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-600">
          <div>总座位数: <strong>{rows * cols}</strong></div>
        </div>
      </Card>

      {/* 排序方式选择 */}
      <div>
        <div className="text-sm text-gray-600 mb-2">排序方式</div>
        <Select
          value={sortingMode}
          onChange={onSortingModeChange}
          className="w-full"
          size="large"
          optionLabelProp="label"
        >
          {sortingModes.map(mode => (
            <Option key={mode.value} value={mode.value} label={mode.label}>
              <div className="flex items-center py-1">
                <span className="mr-2 flex-shrink-0" style={{ color: mode.color }}>
                  {mode.icon}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{mode.label}</div>
                  <div className="text-xs text-gray-500 truncate">{mode.description}</div>
                </div>
              </div>
            </Option>
          ))}
        </Select>
      </div>

      {/* 执行排序按钮 */}
      <Button
        type="primary"
        size="large"
        block
        onClick={onArrange}
        loading={isArranging}
        className="h-12 text-lg font-medium"
      >
        {isArranging ? '正在排序...' : '执行排序'}
      </Button>

      {/* 排序建议 */}
      <Card title="排序建议" size="small">
        <div className="space-y-2 text-sm text-gray-600">
          <Tooltip title="适合需要经常调换座位的班级">
            <div className="flex items-center">
              <SwapOutlined className="mr-2 text-blue-500" />
              <span>随机排序适合促进同学交流</span>
            </div>
          </Tooltip>
          
          <Tooltip title="避免前排同学被后排高个同学挡住视线">
            <div className="flex items-center">
              <ArrowUpOutlined className="mr-2 text-green-500" />
              <span>身高排序有利于视线</span>
            </div>
          </Tooltip>
          
          <Tooltip title="成绩好的同学坐前排，便于听课和管理">
            <div className="flex items-center">
              <TrophyOutlined className="mr-2 text-yellow-500" />
              <span>成绩排序便于管理</span>
            </div>
          </Tooltip>
          
          <Tooltip title="有利于性格互补，减少课堂纪律问题">
            <div className="flex items-center">
              <UsergroupAddOutlined className="mr-2 text-pink-500" />
              <span>男女均衡有利于纪律</span>
            </div>
          </Tooltip>
        </div>
      </Card>

      {/* 快捷操作 */}
      <div>
        <div className="text-sm text-gray-600 mb-2">快捷操作</div>
        <Space size="small" wrap className="w-full">
          <Button 
            size="small" 
            onClick={() => {
              onSortingModeChange('random');
              onArrange();
            }}
            disabled={isArranging}
          >
            快速随机
          </Button>
          <Button 
            size="small" 
            onClick={() => {
              onSortingModeChange('height');
              onArrange();
            }}
            disabled={isArranging}
          >
            按身高排
          </Button>
          <Button 
            size="small" 
            onClick={() => {
              onSortingModeChange('grade');
              onArrange();
            }}
            disabled={isArranging}
          >
            按成绩排
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default SortingOptions;
